import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout';
import { EmptyState } from '@/components/ui/error-boundary';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { hasPlaceholderPermission, usePermission } from '@/lib/permissions';
import {
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Filter,
  Calendar,
  MessageSquare,
  Plus,
  ClipboardList,
  Ban,
  RotateCcw,
} from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { format, formatDistanceToNow, isFuture, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { parseDateOnly, toDateKey } from '@/lib/date';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AbsencesPageHeader } from '@/features/absences/components/absences-page-header';
import { useCreateAbsenceRequestDialog } from '@/features/absences/context/create-absence-request-dialog-context';
import type { AbsenceRequest, AbsenceRequestStatus } from '@hygge/shared';
import axios from 'axios';
import { toast } from 'sonner';
import { useEmployeeNameFormatter } from '@/lib/employee-name';

const REQUESTS_VIEW_STORAGE_KEY = 'absence-requests-view';

type RequestView = 'pending' | 'history' | 'my';

function getInitialView(): RequestView {
  if (typeof window === 'undefined') return 'pending';
  const params = new URLSearchParams(window.location.search);
  const view = params.get('view') as RequestView | null;
  if (view && ['pending', 'history', 'my'].includes(view)) return view;
  const stored = localStorage.getItem(REQUESTS_VIEW_STORAGE_KEY) as RequestView | null;
  if (stored && ['pending', 'history', 'my'].includes(stored)) return stored;
  return 'pending';
}

export const Route = createFileRoute('/absences/request')({
  beforeLoad: async () => {
    const { isAuthenticated, isInitialized } = useAuthStore.getState();
    if (!isInitialized) return;
    if (!isAuthenticated) throw redirect({ to: '/login' });

    const user = useAuthStore.getState().user;
    if (hasPlaceholderPermission(user)) throw redirect({ to: '/placeholder' });
  },
  component: AbsenceRequestPage,
});

// Fetch functions
async function getMyRequests() {
  const { data } = await api.get<AbsenceRequest[]>('/absence-requests/me');
  return data;
}

async function getPendingForApproval() {
  const { data } = await api.get<AbsenceRequest[]>('/absence-requests/pending-for-approval');
  return data;
}

async function getApprovalHistory() {
  const { data } = await api.get<AbsenceRequest[]>('/absence-requests/approval-history');
  return data;
}

function AbsenceRequestPage() {
  const formatName = useEmployeeNameFormatter();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedRequest, setSelectedRequest] = useState<AbsenceRequest | null>(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject' | null>(null);
  const [approvalComment, setApprovalComment] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [recallDialogOpen, setRecallDialogOpen] = useState(false);
  const [view, setView] = useState<RequestView>(getInitialView);

  const { isAuthenticated, user } = useAuthStore();
  const { openDialog: openCreateDialog } = useCreateAbsenceRequestDialog();
  const queryClient = useQueryClient();
  const currentEmployeeId = user?.employee?.id;

  // Fetch data
  const { data: myRequests = [], isLoading: myRequestsLoading } = useQuery({
    queryKey: ['absence-requests', 'me'],
    queryFn: getMyRequests,
    enabled: isAuthenticated,
  });

  const { data: pendingRequests = [], isLoading: pendingLoading } = useQuery({
    queryKey: ['absence-requests', 'pending'],
    queryFn: getPendingForApproval,
    enabled: isAuthenticated,
  });

  const { data: approvalHistory = [], isLoading: historyLoading } = useQuery({
    queryKey: ['absence-requests', 'approval-history'],
    queryFn: getApprovalHistory,
    enabled: isAuthenticated,
  });

  // Sync view from URL on mount (e.g. shared link)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view') as RequestView | null;
    if (viewParam && ['pending', 'history', 'my'].includes(viewParam)) {
      setView(viewParam);
    }
  }, []);

  // Smart default: if pending empty but my has items, switch to my (only on first load)
  useEffect(() => {
    if (myRequestsLoading || pendingLoading) return;
    const hasStored = localStorage.getItem(REQUESTS_VIEW_STORAGE_KEY);
    const hasUrlView = new URLSearchParams(window.location.search).get('view');
    if (hasStored || hasUrlView) return;

    const hasPending = pendingRequests.length > 0;
    const hasMine = myRequests.length > 0;
    if (!hasPending && hasMine) {
      setView('my');
      localStorage.setItem(REQUESTS_VIEW_STORAGE_KEY, 'my');
    }
  }, [
    myRequestsLoading,
    pendingLoading,
    pendingRequests.length,
    myRequests.length,
  ]);

  const handleViewChange = (newView: RequestView) => {
    setView(newView);
    localStorage.setItem(REQUESTS_VIEW_STORAGE_KEY, newView);
    const url = new URL(window.location.href);
    url.searchParams.set('view', newView);
    window.history.replaceState({}, '', url);
  };

  // Filter and search
  const filteredMyRequests = useMemo(() => {
    let filtered = myRequests;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.employee?.firstName?.toLowerCase().includes(query) ||
          r.employee?.lastName?.toLowerCase().includes(query) ||
          r.absenceType?.name?.toLowerCase().includes(query) ||
          r.notes?.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    const activeStatuses = new Set(['PENDING', 'APPROVED']);
    return [...filtered].sort((a, b) => {
      const aActive = activeStatuses.has(a.status) ? 0 : 1;
      const bActive = activeStatuses.has(b.status) ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [myRequests, searchQuery, statusFilter]);

  const filteredPendingRequests = useMemo(() => {
    let filtered = pendingRequests;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.employee?.firstName?.toLowerCase().includes(query) ||
          r.employee?.lastName?.toLowerCase().includes(query) ||
          r.absenceType?.name?.toLowerCase().includes(query) ||
          r.notes?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [pendingRequests, searchQuery]);

  const invalidateAbsenceQueries = (employeeId: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['absence-requests'] }),
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] }),
      queryClient.invalidateQueries({ queryKey: ['employee-assigned-absence-types', employeeId] }),
      queryClient.invalidateQueries({ queryKey: ['employee-policies', employeeId] }),
      queryClient.invalidateQueries({ queryKey: ['employee-absences', employeeId] }),
    ]);

  // Mutations
  const approveMutation = useMutation({
    mutationFn: async ({ id, comment }: { id: string; comment?: string; employeeId: string }) => {
      await api.post(`/absence-requests/${id}/approve`, { comment });
    },
    onSuccess: (_data, { employeeId }) => {
      invalidateAbsenceQueries(employeeId);
      setApprovalDialogOpen(false);
      setSelectedRequest(null);
      setApprovalComment('');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, comment }: { id: string; comment?: string; employeeId: string }) => {
      await api.post(`/absence-requests/${id}/reject`, { comment });
    },
    onSuccess: (_data, { employeeId }) => {
      invalidateAbsenceQueries(employeeId);
      setApprovalDialogOpen(false);
      setSelectedRequest(null);
      setApprovalComment('');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async ({ id }: { id: string; employeeId: string }) => {
      await api.delete(`/absence-requests/${id}`);
    },
    onSuccess: (_data, { employeeId }) => {
      invalidateAbsenceQueries(employeeId);
      setCancelDialogOpen(false);
      setSelectedRequest(null);
    },
  });

  // Recall is the "take back an already-approved request" flow. The backend is
  // the authority (`absenceType.allowRecall` + `recallDeadlineDays`), and the
  // button mirrors that rule so it can't be clicked into a certain 400
  // (`getRecallBlockReason`). The toast stays as the backstop for the cases the
  // client can't see — a stale list, or the type's config changing under us.
  const recallMutation = useMutation({
    mutationFn: async ({ id }: { id: string; employeeId: string }) => {
      await api.post(`/absence-requests/${id}/recall`);
    },
    onSuccess: async (_data, { employeeId }) => {
      setRecallDialogOpen(false);
      setSelectedRequest(null);
      await invalidateAbsenceQueries(employeeId);
      toast.success('Request recalled — balance restored');
    },
    onError: (err: unknown) => {
      let message = 'Failed to recall request';
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { message?: unknown } | undefined;
        if (typeof data?.message === 'string') message = data.message;
      }
      toast.error(message);
    },
  });

  const handleApproveClick = (request: AbsenceRequest) => {
    setSelectedRequest(request);
    setApprovalAction('approve');
    setApprovalDialogOpen(true);
  };

  const handleRejectClick = (request: AbsenceRequest) => {
    setSelectedRequest(request);
    setApprovalAction('reject');
    setApprovalDialogOpen(true);
  };

  const handleCancelClick = (request: AbsenceRequest) => {
    setSelectedRequest(request);
    setCancelDialogOpen(true);
  };

  const handleCancelSubmit = () => {
    if (!selectedRequest) return;
    cancelMutation.mutate({ id: selectedRequest.id, employeeId: selectedRequest.employeeId });
  };

  const handleRecallClick = (request: AbsenceRequest) => {
    setSelectedRequest(request);
    setRecallDialogOpen(true);
  };

  const handleRecallSubmit = () => {
    if (!selectedRequest) return;
    recallMutation.mutate({ id: selectedRequest.id, employeeId: selectedRequest.employeeId });
  };

  const handleApprovalSubmit = () => {
    if (!selectedRequest) return;

    if (approvalAction === 'approve') {
      approveMutation.mutate({
        id: selectedRequest.id,
        comment: approvalComment || undefined,
        employeeId: selectedRequest.employeeId,
      });
    } else {
      rejectMutation.mutate({
        id: selectedRequest.id,
        comment: approvalComment || undefined,
        employeeId: selectedRequest.employeeId,
      });
    }
  };

  return (
    <AuthGuard>
      <AppLayout>
        <AbsencesPageHeader />
        <div className="min-h-full animate-fade-in bg-background">
          <div className="mx-auto max-w-6xl px-6 py-6">
            {/* Page Header - simplified, with primary action */}
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                  Requests
                </h1>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Review and manage absence requests
                </p>
              </div>
              <Button
                onClick={openCreateDialog}
                className="gap-2 shadow-lg"
              >
                <Plus className="h-4 w-4" />
                New Request
              </Button>
            </div>

            {/* Filter pills + Search/Filter bar */}
            <div className="space-y-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
                {/* Filter pills - not tabs, just view filters */}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleViewChange('pending')}
                    className={cn(
                      'inline-flex h-8 items-center rounded-full px-3.5 text-sm font-medium transition-colors',
                      view === 'pending'
                        ? 'bg-amber-400 text-amber-950 dark:text-amber-950 ring-1 ring-amber-500/50'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    Pending
                    {pendingRequests.length > 0 && (
                      <span
                        className={cn(
                          'ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-medium',
                          view === 'pending'
                            ? 'bg-amber-950/15 text-amber-950 dark:text-amber-950'
                            : 'bg-muted text-muted-foreground'
                        )}
                      >
                        {pendingRequests.length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewChange('history')}
                    className={cn(
                      'inline-flex h-8 items-center rounded-full px-3.5 text-sm font-medium transition-colors',
                      view === 'history'
                        ? 'bg-primary/10 text-primary ring-1 ring-primary/20'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    History
                    {approvalHistory.length > 0 && (
                      <span
                        className={cn(
                          'ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-medium',
                          view === 'history' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                        )}
                      >
                        {approvalHistory.length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewChange('my')}
                    className={cn(
                      'inline-flex h-8 items-center rounded-full px-3.5 text-sm font-medium transition-colors',
                      view === 'my'
                        ? 'bg-primary/10 text-primary ring-1 ring-primary/20'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    My Requests
                    {myRequests.length > 0 && (
                      <span
                        className={cn(
                          'ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-medium',
                          view === 'my' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                        )}
                      >
                        {myRequests.length}
                      </span>
                    )}
                  </button>
                </div>

                {/* Search + Status filter (when viewing My Requests) */}
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-8 w-[180px] border border-border bg-background pl-8 text-sm sm:w-[220px]"
                    />
                  </div>
                  {view === 'my' && (
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="h-8 w-[130px] text-xs">
                        <Filter className="mr-1.5 h-3.5 w-3.5" />
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="PENDING">Pending</SelectItem>
                        <SelectItem value="APPROVED">Approved</SelectItem>
                        <SelectItem value="REJECTED">Rejected</SelectItem>
                        <SelectItem value="CANCELLED">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {/* Content based on view - smooth fade on switch */}
              {view === 'pending' && (
                <div key="pending" className="mt-6 animate-fade-in" >
                {pendingLoading ? (
                  <div className="flex items-center justify-center py-24">
                    <div className="text-sm text-muted-foreground">Loading requests...</div>
                  </div>
                ) : filteredPendingRequests.length === 0 ? (
                  <EmptyState
                    icon={<Clock className="h-12 w-12" />}
                    title="No pending requests"
                    description={
                      searchQuery
                        ? 'No requests match your search criteria.'
                        : myRequests.length > 0
                          ? `You have ${myRequests.length} request${myRequests.length === 1 ? '' : 's'} in My Requests.`
                          : 'All requests have been processed. You\'re all caught up!'
                    }
                    action={
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        {myRequests.length > 0 ? (
                          <Button
                            variant="outline"
                            onClick={() => handleViewChange('my')}
                            className="gap-2"
                          >
                            <ClipboardList className="h-4 w-4" />
                            View My Requests
                          </Button>
                        ) : null}
                        <Button onClick={openCreateDialog} className="gap-2">
                          <Plus className="h-4 w-4" />
                          New Request
                        </Button>
                        <Button variant="ghost" asChild className="gap-2">
                          <Link to="/absences/calendar">
                            <Calendar className="h-4 w-4" />
                            View Calendar
                          </Link>
                        </Button>
                      </div>
                    }
                  />
                ) : (
                  <div className="divide-y divide-border rounded-lg border border-border bg-background">
                    {filteredPendingRequests.map((request) => (
                      <RequestCard
                        key={request.id}
                        request={request}
                        onApprove={() => handleApproveClick(request)}
                        onReject={() => handleRejectClick(request)}
                        showActions={true}
                      />
                    ))}
                  </div>
                )}
                </div>
              )}

              {view === 'history' && (
                <div key="history" className="mt-6 animate-fade-in" >
                {historyLoading ? (
                  <div className="flex items-center justify-center py-24">
                    <div className="text-sm text-muted-foreground">Loading history...</div>
                  </div>
                ) : approvalHistory.length === 0 ? (
                  <EmptyState
                    icon={<CheckCircle2 className="h-12 w-12" />}
                    title="No approval history"
                    description="Your approval decisions will appear here once you start reviewing requests."
                  />
                ) : (
                  <div className="divide-y divide-border rounded-lg border border-border bg-background">
                    {approvalHistory.map((request) => (
                      <RequestCard key={request.id} request={request} showActions={false} />
                    ))}
                  </div>
                )}
                </div>
              )}

              {view === 'my' && (
                <div key="my" className="mt-6 animate-fade-in" >
                {myRequestsLoading ? (
                  <div className="flex items-center justify-center py-24">
                    <div className="text-sm text-muted-foreground">Loading your requests...</div>
                  </div>
                ) : filteredMyRequests.length === 0 ? (
                  <EmptyState
                    icon={<Calendar className="h-12 w-12" />}
                    title="No requests yet"
                    description={
                      searchQuery
                        ? 'No requests match your search. Try different keywords.'
                        : 'You haven\'t created any absence requests yet.'
                    }
                    action={
                      !searchQuery && (
                        <Button onClick={openCreateDialog} className="mt-2 gap-2">
                          <Plus className="h-4 w-4" />
                          New Request
                        </Button>
                      )
                    }
                  />
                ) : (
                  <div className="divide-y divide-border rounded-lg border border-border bg-background">
                    {filteredMyRequests.map((request) => (
                      <RequestCard
                        key={request.id}
                        request={request}
                        showActions={false}
                        onCancel={
                          request.status === 'PENDING' && request.employee?.id === currentEmployeeId
                            ? () => handleCancelClick(request)
                            : undefined
                        }
                        // Recall button: visible on APPROVED requests for the
                        // owner of the request. Backend gates the actual recall
                        // (allowRecall + recallDeadlineDays + start_date); the
                        // card keeps the button in place but inactive when that
                        // rule already says no, so the affordance stays
                        // discoverable without being a dead click. [HW-783]
                        onRecall={
                          request.status === 'APPROVED' && request.employee?.id === currentEmployeeId
                            ? () => handleRecallClick(request)
                            : undefined
                        }
                        isCancelling={cancelMutation.isPending && selectedRequest?.id === request.id}
                        isRecalling={recallMutation.isPending && selectedRequest?.id === request.id}
                      />
                    ))}
                  </div>
                )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Approval Dialog */}
        <AlertDialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
          <AlertDialogContent className="sm:max-w-[480px]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lg font-semibold">
                {approvalAction === 'approve' ? 'Approve Request' : 'Reject Request'}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm text-muted-foreground">
                {approvalAction === 'approve'
                  ? 'This request will be approved and the absence will be recorded.'
                  : 'This request will be rejected. The employee will be notified.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {selectedRequest && (
              <div className="space-y-4 py-2">
                {/* Request summary */}
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9 shrink-0 ring-1 ring-border">
                      <AvatarImage src={selectedRequest.employee?.avatarUrl || undefined} />
                      <AvatarFallback className="text-xs font-medium">
                        {selectedRequest.employee?.firstName?.[0]}
                        {selectedRequest.employee?.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">
                        {selectedRequest.employee ? formatName({ firstName: selectedRequest.employee.firstName, lastName: selectedRequest.employee.lastName }) : ''}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ backgroundColor: selectedRequest.absenceType?.color || '#6b7280' }}
                        />
                        <span className="text-xs text-muted-foreground truncate">
                          {selectedRequest.absenceType?.name}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-4 pt-3 border-t border-border">
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5">Duration</div>
                      <div className="text-sm font-medium">{selectedRequest.totalDays} days</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5">Dates</div>
                      <div className="text-sm font-medium">
                        {format(parseDateOnly(selectedRequest.startDate), 'MMM d')} -{' '}
                        {format(parseDateOnly(selectedRequest.endDate), 'MMM d')}
                      </div>
                    </div>
                  </div>
                  {selectedRequest.notes && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="text-xs text-muted-foreground mb-1">Notes</div>
                      <p className="text-sm text-foreground leading-relaxed">{selectedRequest.notes}</p>
                    </div>
                  )}
                </div>
                {/* Comment input */}
                <div className="space-y-2">
                  <Label htmlFor="comment" className="text-xs font-medium">
                    Comment {approvalAction === 'reject' && <span className="text-muted-foreground">(recommended)</span>}
                  </Label>
                  <Textarea
                    id="comment"
                    placeholder={
                      approvalAction === 'approve'
                        ? 'Add an optional comment...'
                        : 'Explain why this request is being rejected...'
                    }
                    value={approvalComment}
                    onChange={(e) => setApprovalComment(e.target.value)}
                    rows={3}
                    className="resize-none text-sm"
                  />
                </div>
              </div>
            )}
            <AlertDialogFooter className="gap-2">
              <AlertDialogCancel className="h-9">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleApprovalSubmit}
                // Approve is the main action, Reject the destructive one: the
                // action's own default is `destructive`, which would draw both
                // in coral. [HW-926]
                variant={approvalAction === 'reject' ? 'destructive' : 'default'}
                className="h-9"
                disabled={approveMutation.isPending || rejectMutation.isPending}
              >
                {approveMutation.isPending || rejectMutation.isPending
                  ? 'Processing...'
                  : approvalAction === 'approve'
                    ? 'Approve'
                    : 'Reject'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Cancel Dialog */}
        <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <AlertDialogContent className="sm:max-w-[400px]">
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Request</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to cancel this absence request? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2">
              <AlertDialogCancel className="h-9">Keep</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancelSubmit}
                className="h-9 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Request'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Recall Dialog — for already-approved requests */}
        <AlertDialog open={recallDialogOpen} onOpenChange={setRecallDialogOpen}>
          <AlertDialogContent className="sm:max-w-[420px]">
            <AlertDialogHeader>
              <AlertDialogTitle>Recall Approved Request</AlertDialogTitle>
              <AlertDialogDescription>
                This request was already approved. Recalling restores your balance and notifies the approver.
                Allowed only before the absence starts (subject to the recall deadline configured on the absence type).
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2">
              <AlertDialogCancel className="h-9">Keep</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleRecallSubmit}
                className="h-9 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={recallMutation.isPending}
              >
                {recallMutation.isPending ? 'Recalling...' : 'Recall Request'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </AppLayout>
    </AuthGuard>
  );
}

/**
 * Why recall is unavailable for an approved request, or null when it can be
 * attempted:
 *   - the absence type has recall turned off,
 *   - the type's recall deadline — `recallDeadlineDays` before the start date —
 *     has passed,
 *   - the absence has already started (its first day counts as started), unless
 *     the caller holds `absences.requests.edit-past`.
 *
 * The first two mirror the server rule (`requests.service.ts` → `recall()`), so
 * the button no longer offers a call that ends in a 400. The third is the rule
 * the server gains in HW-789, with the same `edit-past` exemption: until that
 * lands the endpoint still accepts recalling an absence that has started (201,
 * days credited back) on any type with no deadline configured, so until then the
 * UI is deliberately the stricter side — recall is meant for taking a request
 * back before it begins, which its own dialog has always promised.
 *
 * Neither the deadline nor the start day is judged by the viewer's calendar. The
 * deadline is an instant, computed the way the server computes it; the start day
 * is compared as a UTC calendar day, the basis the server uses for every
 * date-only comparison. Using local days instead would disagree with the
 * endpoint by the viewer's UTC offset in both directions — west of UTC it would
 * leave the button live for the last hours of the window (the very 400 this
 * guards against), east of UTC it would close the window early.
 *
 * When `absenceType` is missing from the payload the two config-driven checks
 * are skipped — there is nothing to read — and only the start date is
 * considered; anything else is left to the server to answer.
 */
function getRecallBlockReason(request: AbsenceRequest, canEditPast: boolean): string | null {
  const absenceType = request.absenceType;

  if (absenceType && !absenceType.allowRecall) {
    return 'Recall is turned off for this absence type';
  }

  // Checked before the start date so the message names the configured rule the
  // caller ran into rather than the more generic "it already started".
  const deadlineDays = absenceType?.recallDeadlineDays ?? null;
  if (deadlineDays) {
    // The server takes the UTC-midnight start date and steps back N days
    // (`requests.service.ts` → `recall()`), then compares that instant to "now",
    // so the cutoff is the same moment for every viewer — the deployed backend
    // runs in UTC, where this arithmetic is identical to the server's. (A backend
    // in local time puts its cutoff an hour off when a DST change falls inside
    // the span; the miss is at most that hour, either way.)
    const deadlineAt =
      Date.parse(`${toDateKey(request.startDate)}T00:00:00.000Z`) -
      deadlineDays * 24 * 60 * 60 * 1000;
    if (Date.now() > deadlineAt) {
      // The cutoff is an instant, not a calendar day — naming a date would be a
      // day out for viewers either side of UTC, so the message states the rule.
      return `Recall closed — the deadline is ${deadlineDays} ${
        deadlineDays === 1 ? 'day' : 'days'
      } before the start date`;
    }
  }

  // Both sides compare UTC calendar days — `toDateKey` for the request's stored
  // day and for today, the same basis as the server's `startOfUtcDay`. Comparing
  // `yyyy-MM-dd` strings is exact, so the button and the endpoint flip on the
  // same instant. The message states the rule rather than asserting the absence
  // has begun: on the UTC basis that would read a day early west of UTC.
  if (!canEditPast && toDateKey(request.startDate) <= toDateKey(new Date())) {
    return "Recall closes when the absence's first day begins";
  }

  return null;
}

// Request Card Component
interface RequestCardProps {
  request: AbsenceRequest;
  onApprove?: () => void;
  onReject?: () => void;
  onCancel?: () => void;
  onRecall?: () => void;
  showActions?: boolean;
  isCancelling?: boolean;
  isRecalling?: boolean;
}

function RequestCard({
  request,
  onApprove,
  onReject,
  onCancel,
  onRecall,
  showActions = false,
  isCancelling = false,
  isRecalling = false,
}: RequestCardProps) {
  const formatName = useEmployeeNameFormatter();
  const startDate = parseDateOnly(request.startDate);
  const endDate = parseDateOnly(request.endDate);
  const isUpcoming = isFuture(startDate);
  const daysUntilStart = differenceInDays(startDate, new Date());
  // Read here rather than passed in: the exemption belongs to the rule, and the
  // hook reads the already-loaded permission list from the auth store.
  const canEditPast = usePermission('absences.requests.edit-past');
  const recallBlockReason = onRecall ? getRecallBlockReason(request, canEditPast) : null;
  const recallBlocked = isRecalling || recallBlockReason !== null;
  const statusConfig = getStatusConfig(request.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="group relative flex items-center gap-4 px-4 py-3 transition-colors hover:bg-muted/30">
      {/* Status indicator */}
      <div className={cn('h-full w-0.5 rounded-full', statusConfig.dotColor)} />

      {/* Avatar */}
      <Avatar className="h-8 w-8 shrink-0 ring-1 ring-border">
        <AvatarImage src={request.employee?.avatarUrl || undefined} />
        <AvatarFallback className="text-xs font-medium">
          {request.employee?.firstName?.[0]}
          {request.employee?.lastName?.[0]}
        </AvatarFallback>
      </Avatar>

      {/* Main content */}
      <div className="flex flex-1 items-center justify-between gap-4 min-w-0">
        <div className="flex flex-1 items-center gap-4 min-w-0">
          {/* Employee name and type */}
          <div className="flex flex-1 flex-col min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground truncate">
                {request.employee ? formatName({ firstName: request.employee.firstName, lastName: request.employee.lastName }) : ''}
              </span>
              <div
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: request.absenceType?.color || '#6b7280' }}
              />
              <span className="text-sm text-muted-foreground truncate">
                {request.absenceType?.name}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-0.5">
              <span className="text-xs text-muted-foreground">
                {format(startDate, 'MMM d')} - {format(endDate, 'MMM d')}
              </span>
              <span className="text-xs font-medium text-muted-foreground">
                {request.totalDays} {request.totalDays === 1 ? 'day' : 'days'}
              </span>
              {request.employee?.department && (
                <>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs text-muted-foreground truncate">
                    {request.employee.department.name}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Status badge */}
          <div className="flex items-center gap-2 shrink-0">
            <div className={cn('flex items-center gap-1.5 rounded-md px-2 py-0.5', statusConfig.className)}>
              <StatusIcon className="h-3 w-3" />
              <span className="text-xs font-medium">{statusConfig.label}</span>
            </div>
          </div>
        </div>

        {/* Actions - approve/reject for pending (approver view) */}
        {showActions && request.status === 'PENDING' && (
          <TooltipProvider delayDuration={150}>
            <div className="flex items-center gap-2 shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={onReject}
                    className="h-8 w-8 rounded-full border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground hover:border-destructive dark:bg-transparent dark:border-destructive/40 dark:hover:bg-destructive dark:hover:border-destructive transition-colors"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Reject</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={onApprove}
                    className="h-8 w-8 rounded-full border-green-500/40 text-green-600 hover:bg-green-600 hover:text-white hover:border-green-600 dark:bg-transparent dark:border-green-500/40 dark:hover:bg-green-600 dark:hover:border-green-600 transition-colors"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Approve</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        )}

        {/* Metadata */}
        <div className="flex flex-col items-end gap-0.5 shrink-0">
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
          </span>
          {isUpcoming && daysUntilStart > 0 && (
            <span className="text-xs font-medium text-amber-600 dark:text-amber-400 whitespace-nowrap">
              in {daysUntilStart}d
            </span>
          )}
        </div>
      </div>

      {/* Notes tooltip */}
      {request.notes && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="shrink-0 cursor-pointer">
                <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-sm">
              {request.notes}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Cancel for own pending requests (My Requests view) - at the end */}
      {onCancel && request.status === 'PENDING' && (
        <div className="shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isCancelling}
            className="h-7 w-7 rounded-full text-xs text-muted-foreground hover:bg-muted hover:text-destructive"
            title="Cancel request"
          >
            <Ban className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Recall for own approved requests. The button stays visible but goes
          inactive once the server would refuse the call (see
          `getRecallBlockReason`), with the reason in the tooltip — previously it
          was always live and the refusal only arrived as a toast after the
          click. [HW-783] */}
      {onRecall && request.status === 'APPROVED' && (
        <div className="shrink-0">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              {/* The wrapper is the tooltip trigger and takes the tab stop while
                  the button is disabled: a disabled button neither receives
                  pointer events nor keeps its place in the tab order, so the
                  reason would be unreachable. Same pattern as the self-delete
                  guard in `settings.roles.tsx`. The reason is repeated as the
                  accessible name, which is how a screen reader gets it; Radix
                  does not open tooltips on touch, so a tap gives no hint. */}
              <TooltipTrigger asChild>
                <span tabIndex={recallBlocked ? 0 : -1} className="inline-flex">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRecall}
                    disabled={recallBlocked}
                    aria-label={recallBlockReason ?? 'Withdraw this approved request'}
                    className="h-7 w-7 rounded-full text-xs text-muted-foreground hover:bg-muted hover:text-destructive"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {recallBlockReason ?? 'Withdraw this approved request'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}

function getStatusConfig(status: AbsenceRequestStatus) {
  const configs = {
    PENDING: {
      label: 'Pending',
      icon: Clock,
      // Amber washes out on the warm cream background, and amber-on-amber text
      // is low-contrast. The pending pill (which also flags "needs action")
      // gets a solid bright fill with near-black text so it's clearly legible.
      className: 'bg-amber-400 text-amber-950 dark:bg-amber-400 dark:text-amber-950',
      dotColor: 'bg-amber-500',
    },
    APPROVED: {
      label: 'Approved',
      icon: CheckCircle2,
      className: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20',
      dotColor: 'bg-green-500',
    },
    REJECTED: {
      label: 'Rejected',
      icon: XCircle,
      className: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20',
      dotColor: 'bg-red-500',
    },
    CANCELLED: {
      label: 'Cancelled',
      icon: XCircle,
      className: 'bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20',
      dotColor: 'bg-gray-500',
    },
    RECALLED: {
      label: 'Recalled',
      icon: Clock,
      className: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20',
      dotColor: 'bg-blue-500',
    },
  };
  return configs[status];
}
